import { Modal, Drawer, type ModalProps, type DrawerProps } from 'antd';
import { useEffect, useRef } from 'react';
import './CrmOverlay.css';
import { cycleOverlayTab } from '../../ui/overlayFocus';

type OverlaySize = 'small' | 'medium' | 'large' | 'extraLarge';
const modalWidths: Record<OverlaySize, number> = { small: 400, medium: 560, large: 800, extraLarge: 1100 };
const drawerWidths: Record<OverlaySize, number> = { small: 360, medium: 520, large: 760, extraLarge: 1100 };
type CloseGuard = () => boolean | Promise<boolean>;
export type CrmModalProps = ModalProps & { crmSize?: OverlaySize; canClose?: CloseGuard };
export type CrmDrawerProps = DrawerProps & { crmSize?: OverlaySize; canClose?: CloseGuard };

function nestedEscape(event: { target: EventTarget | null; defaultPrevented: boolean; key?: string }) {
  return 'key' in event && event.key === 'Escape' && (event.defaultPrevented ||
    (event.target instanceof Element && Boolean(event.target.closest('[data-aa-searchable="true"], [aria-expanded="true"]'))));
}

function useGuardedClose<T extends { target: EventTarget | null; defaultPrevented: boolean; key?: string }>(open: boolean | undefined, canClose: CloseGuard | undefined, onClose: ((event: T) => void) | undefined) {
  const pending = useRef(false);
  const generation = useRef(0);
  useEffect(() => { generation.current += 1; pending.current = false; return () => { generation.current += 1; }; }, [open]);
  return (event: T) => {
    if (nestedEscape(event) || pending.current) return;
    if (!canClose) { onClose?.(event); return; }
    pending.current = true;
    const version = generation.current;
    let result: boolean | Promise<boolean>;
    try { result = canClose(); } catch { pending.current = false; return; }
    if (typeof result === 'boolean') {
      pending.current = false;
      if (result) onClose?.(event);
      return;
    }
    void Promise.resolve(result).then(allowed => {
      if (generation.current !== version) return;
      pending.current = false;
      if (allowed) onClose?.(event);
    }, () => { if (generation.current === version) pending.current = false; });
  };
}

/** Shared presentation over Ant Design; callers retain close guards and business actions. */
export function CrmModal({ crmSize = 'medium', width, canClose, onCancel, className, modalRender, ...props }: CrmModalProps) {
  const requestClose = useGuardedClose(props.open, canClose, onCancel);
  const trap = props.focusable?.trap ?? (typeof props.mask === 'object' ? props.mask.enabled !== false : props.mask !== false);
  return <Modal {...props} className={['crm-modal', className].filter(Boolean).join(' ')}
    onCancel={requestClose}
    modalRender={node => <div role="presentation" style={{ display: 'contents' }} onKeyDown={trap ? cycleOverlayTab : undefined}>{modalRender ? modalRender(node) : node}</div>}
    width={width ?? `min(${modalWidths[crmSize]}px, calc(100vw - 32px))`} />;
}
export function CrmDrawer({ crmSize = 'medium', width, size, canClose, onClose, className, drawerRender, ...props }: CrmDrawerProps) {
  const requestClose = useGuardedClose(props.open, canClose, onClose);
  const trap = props.focusable?.trap ?? (typeof props.mask === 'object' ? props.mask.enabled !== false : props.mask !== false);
  return <Drawer {...props} className={['crm-drawer', className].filter(Boolean).join(' ')}
    onClose={requestClose}
    drawerRender={node => <div role="presentation" style={{ display: 'contents' }} onKeyDown={trap ? cycleOverlayTab : undefined}>{drawerRender ? drawerRender(node) : node}</div>}
    size={width ?? size ?? `min(${drawerWidths[crmSize]}px, 100vw)`} />;
}
